import { Loader2 } from "lucide-react";
import type { HTMLAttributes } from "react";
import { cn } from "../lib/cn";

export function Spinner({ className, ...props }: HTMLAttributes<SVGSVGElement>) {
  return (
    <Loader2
      className={cn("h-5 w-5 animate-spin text-muted-foreground", className)}
      aria-label="Loading"
      {...props}
    />
  );
}

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("relative overflow-hidden rounded-md bg-muted/70", className)}
      aria-hidden
      {...props}
    >
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/25 dark:via-white/10 to-transparent" />
    </div>
  );
}

export function SkeletonCard({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("rounded-xl border border-border bg-card p-5 space-y-4 shadow-xs", className)} {...props}>
      <Skeleton className="h-6 w-1/3" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
      </div>
    </div>
  );
}

export interface SkeletonTableProps extends HTMLAttributes<HTMLDivElement> {
  rows?: number;
  cols?: number;
}

export function SkeletonTable({ className, rows = 5, cols = 4, ...props }: SkeletonTableProps) {
  return (
    <div className={cn("rounded-xl border border-border bg-card overflow-hidden shadow-xs", className)} {...props}>
      <div className="border-b border-border bg-muted/40 p-4 flex gap-4">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={`th-${i}`} className="h-4 flex-1" />
        ))}
      </div>
      <div className="p-4 space-y-4">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={`tr-${r}`} className="flex gap-4">
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton key={`td-${r}-${c}`} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export interface DataTableSkeletonProps extends HTMLAttributes<HTMLDivElement> {
  rows?: number;
  cols?: number;
}

export function DataTableSkeleton({ rows = 6, cols = 5, className, ...props }: DataTableSkeletonProps) {
  return (
    <div className={cn("rounded-xl border border-border bg-card overflow-hidden shadow-xs", className)} {...props}>
      <div className="border-b border-border bg-muted/40 p-3.5 flex items-center gap-4">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton
            key={`th-${i}`}
            className={cn("h-4 rounded", i === 0 ? "w-1/4" : i === cols - 1 ? "w-16 ml-auto" : "flex-1")}
          />
        ))}
      </div>
      <div className="divide-y divide-border/60">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={`tr-${r}`} className="p-3.5 flex items-center gap-4">
            {Array.from({ length: cols }).map((_, c) => (
              <div
                key={`td-${r}-${c}`}
                className={cn(c === 0 ? "w-1/4 space-y-1.5" : c === cols - 1 ? "w-16 ml-auto" : "flex-1")}
              >
                <Skeleton className={cn("h-4 rounded", c % 2 === 0 ? "w-4/5" : "w-3/5")} />
                {c === 0 && <Skeleton className="h-3 w-1/2 rounded" />}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export interface CatalogGridSkeletonProps extends HTMLAttributes<HTMLDivElement> {
  count?: number;
}

export function CatalogGridSkeleton({ count = 8, className, ...props }: CatalogGridSkeletonProps) {
  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-2.5 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
        className
      )}
      {...props}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-border/80 bg-card p-3 space-y-3 shadow-xs animate-in fade-in flex flex-col justify-between"
        >
          <div className="space-y-3">
            <div className="relative aspect-square w-full rounded-lg overflow-hidden bg-muted/60">
              <Skeleton className="w-full h-full" />
              <div className="absolute top-2 left-2">
                <Skeleton className="h-4 w-12 rounded-full" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Skeleton className="h-4 w-4/5 rounded" />
              <Skeleton className="h-3 w-1/2 rounded" />
            </div>

            <div className="flex items-baseline justify-between pt-1">
              <Skeleton className="h-5 w-20 rounded" />
              <Skeleton className="h-3 w-12 rounded" />
            </div>
          </div>

          <div className="pt-2 border-t border-border/50">
            <Skeleton className="h-8 w-full rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function StatCardsSkeleton({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="p-4 rounded-xl border border-border bg-card shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-20 rounded" />
            <Skeleton className="h-8 w-8 rounded-lg" />
          </div>
          <Skeleton className="h-7 w-28 rounded" />
          <Skeleton className="h-3 w-36 rounded" />
        </div>
      ))}
    </div>
  );
}

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  value?: number;
}

export function ProgressBar({ className, value, ...props }: ProgressBarProps) {
  return (
    <div className={cn("relative h-1.5 w-full overflow-hidden rounded-full bg-muted", className)} {...props}>
      {value !== undefined ? (
        <div 
          className="h-full bg-primary transition-all duration-300 ease-in-out"
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      ) : (
        <div className="absolute inset-0 w-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      )}
    </div>
  );
}
